import { FieldTypes } from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import UserForm from "views/IAM/Users/UserForm";
import TeamForm from "views/IAM/Teams/TeamForm";
import TeamMemberDetails from "views/IAM/Teams/TeamMemberDetails";
import React, { forwardRef } from "react";
import classnames from "classnames";
import { CellRenderers, ColumnFilterTypes, getFilterProps } from "utils/tableUtils";
import Vocabulary from 'components/Vocabulary';
import { hasPermission } from "utils/authService";
import { Collapse, Form, Radio, Typography } from "antd";
import { PermissionSetMap } from "constants/RBACConstants";
import _, { some, isEmpty, find, isString, intersection, map } from "lodash";
import AuthorisedContainer from "components/AuthorisedContainer";
import AuthorisedCheckbox from "components/AuthorisedCheckbox";
import User from "components/User";
import { getConfidentialOptions } from "views/GlobalSettings/TierPresetConfiguration";
import { LoadingIcon } from "components/Icons";
import BaseModalForm from "components/BaseModalForm";
import { getFeatureFlags_UNSAFE, useFeatureFlag } from 'contexts/FeatureFlagContext';
import SubProductAPI from "restAPI/SubProductAPI";
import { CUSTOM_TIER, getTagKey, getTagKeyMap } from "utils/tierUtil";
import { getLocalStorage } from "utils/localStorageUtils";
import GlobalSettingAPI from "restAPI/GlobalSettingAPI";
import moment from "moment";
const { Text } = Typography;
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import { ALL_HIERARCHY_KEY, ARMORCODE_GROUP_KEY, getHierarchyName, renderGroups } from "utils/armorcodeGroupUtil";

export function getOwnerFields({ users, activeUser, form, fetchUsers, loading = false, teamsOptions, fetchTeams, type, teamId, setTeamId, showTeamView = true, readOnly, refreshUsers }){

    const onUserAddAction = (e) => {
        const _activeUser = activeUser.sessionData;
        const fieldName = e.currentTarget.name;
        ModalUtil.show({
            content: <UserForm activeUser={_activeUser} />,
            afterClose: createdUser => {
                refreshUsers();
                fetchUsers().promise.then((userMap) => {
                    if (!isEmpty(createdUser)) {
                        const userInCurrentProduct = find(userMap, { value: createdUser.id });
                        if (!isEmpty(userInCurrentProduct)) {
                            form.setFieldsValue({ [fieldName]: createdUser.id });
                        }
                    }
                })
            }
        })
    };

    const onTeamAddAction = (e) => {
        const _activeUser = activeUser.sessionData;
        const fieldName = e.currentTarget.name;
        ModalUtil.show({
            content: <TeamForm activeUser={_activeUser} teamsOptions={teamsOptions} />,
            afterClose: createdTeam => {
                fetchTeams().promise.then((teams) => {
                    if (!isEmpty(createdTeam)) {
                        const teamInCurrentProduct = find(teams, { value: createdTeam.id });
                        if (!isEmpty(teamInCurrentProduct)) {
                            form.setFieldsValue({ [fieldName]: createdTeam.id });
                            if (typeof setTeamId === "function") {
                                setTeamId(createdTeam.id);
                            }
                        }
                    }
                })
            }
        })
    };

    const ownersCommonAttributes = {
        type: FieldTypes.SELECT_WITH_ACTION,
        options: users,
        colSpan: 12,
        loading,
        // The hidden fields are used in team view
        style: showTeamView ? { display: "none" } : undefined,
    }

    const teamsCommonAttributes = {
        ...ownersCommonAttributes,
        options: teamsOptions,
        style: undefined,
        help: type ? <>The <Vocabulary name="team" /> added here will be the Primary <Vocabulary name="team" isLowerCase={true} /> for the <Vocabulary name={type} /></> : "",
    }

    if(readOnly){
        delete teamsCommonAttributes.type;
        teamsCommonAttributes.render = () => {
            if(_.isEmpty(teamsOptions)){
                return <LoadingIcon />;
            }
            if(teamId){
              return _.find(teamsOptions, { value: teamId })?.label ?? teamId;
            }
            return CellRenderers.VALUE_OR_NA();
        }
    }

    if(hasPermission(PermissionSetMap.CREATE_USER)){
        ownersCommonAttributes.btnText = "Add user";
        ownersCommonAttributes.btnType = "dashed";
        ownersCommonAttributes.onActionClick = onUserAddAction;
    }

    if (hasPermission(PermissionSetMap.CREATE_TEAMS)) {
        teamsCommonAttributes.btnText = <>Add <Vocabulary name="team" /></>;
        teamsCommonAttributes.btnType = "dashed";
        teamsCommonAttributes.onActionClick = onTeamAddAction;
    }
    const ownerFields = [{
            name: "businessOwner",
            label: <Vocabulary name="businessOwner" />,
            ...ownersCommonAttributes
        }, {
            name: "securityOwner",
            label: <Vocabulary name="securityOwner" />,
            ...ownersCommonAttributes
        }, {
            name: "engineeringOwner",
            label: <Vocabulary name="engineeringOwner" />,
            ...ownersCommonAttributes
        }, {
            name: "supportOwner",
            label: <Vocabulary name="supportOwner" />,
            ...ownersCommonAttributes
        }, {
            name: "complianceOwner",
            label: <Vocabulary name="complianceOwner" />,
            ...ownersCommonAttributes
        }, 
        ...(showTeamView ? [{
            name: "teamDto",
            label: <Vocabulary name="team" />,
            ...teamsCommonAttributes
        }, {
            name: "members",
            render: () => <TeamMemberDetails teamId={teamId} form={form} readOnly={readOnly} />
        }] : []),
    ];
    return ownerFields;
}

export function getSecurityAndComplianceFields(options = {}, vocabulary = "product") {
    const { onConfidentialityChange, confidentialityData, defaultActiveKey = ['confidentialityRequirement'], permission } = options;
    const q1Text = <>1. Does the <Vocabulary name={vocabulary} /> handle (store / process) data whose loss or exposure would have a major impact on your business operations?</>;
    const q2Text = <>2. Does the <Vocabulary name={vocabulary} /> have controls which limit information access and disclosure to only authorized users, as well as preventing access by, or disclosure to, unauthorized ones?</>;
    const q3Text = <>3. Will any impact or non availability of the <Vocabulary name={vocabulary} /> have a major impact on your business operations?</>;
    const q4Text = <>4. Is the <Vocabulary name={vocabulary} /> providing a service that is deployed in High availability configuration? I.e what will be the impact of non-availability of the <Vocabulary name="product" />?</>;
    const q5Text = <>5. How can the <Vocabulary name={vocabulary} /> be accessed? Or provide interfaces via which Users or Third party components could connect</>;
    const qTextArr = [q1Text, q2Text, q3Text, q4Text, q5Text];

    const commonpProps = {
        render: (field) => {
            return (
                <Form.Item name={field.name} noStyle>
                    <Radio.Group >
                        <Collapse defaultActiveKey={defaultActiveKey} bordered={false} className="m-b-sm">
                            <Collapse.Panel header={<Text strong>{field.custom_label}</Text>} key={field.name} className="no-border">
                                    {
                                        field.options.map((option, i) => {
                                            const Content = (
                                                <div key={option} className={classnames({"m-b-xs p-b-xs border-bottom": i !== field.options.length - 1})}>
                                                    <Radio value={option} disabled={permission && !hasPermission(permission)} className="risk-assessment-questions">
                                                        <>
                                                            <span className="text-bold label">{option}</span> 
                                                            <span className="description">{field.helpTexts?.[i]}</span>
                                                        </>
                                                    </Radio>
                                                </div>
                                            )
                                            return <>
                                                <AuthorisedContainer permission={permission}>
                                                    {Content}
                                                </AuthorisedContainer>
                                            </>
                                        })
                                    }
                            </Collapse.Panel>
                        </Collapse>
                    </Radio.Group>
                </Form.Item>
            )
        }
    }

    const questionFields = [{
        name: "confidentialityRequirement",
        custom_label: q1Text,
        options: ["Not Defined", "Low", "Medium", "High"],
        w: 100,
        helpTexts: [
            "Assigning this value indicates there is insufficient information to choose one of the other values. It has the same effect on scoring as assigning Medium.",
            "Loss of Confidentiality is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
            "Loss of Confidentiality is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
            "Loss of Confidentiality is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
        ],
        ...commonpProps
    }, {
        name: "confidentiality_custom",
        custom_label: q2Text,
        type: "check",
        options: [<>The <Vocabulary name={vocabulary} /> only has data for one Customer / department</>, <>The <Vocabulary name={vocabulary} />  can only be accessed by authenticated Users</>, <>The  <Vocabulary name={vocabulary} /> has Role-based access control</>],
        ...commonpProps,

        render: (field) => {
            return (
                <Collapse defaultActiveKey={defaultActiveKey} bordered={false} className="m-b-sm">
                    <Collapse.Panel header={<Text strong>{field.custom_label}</Text>} key="1" className="no-border">
                            {
                                field.options.map((option, i) => {
                                    return (
                                        <div key={i} className={classnames({"m-b-xs p-b-xs border-bottom": i !== field.options.length - 1})}>
                                            <AuthorisedCheckbox permission={permission} tooltipTitle = "No access" checked={confidentialityData["C"+i]} onChange={(e) => onConfidentialityChange(e, i)}>{option}</AuthorisedCheckbox>
                                        </div>
                                    )
                                })
                            }
                    </Collapse.Panel>
                </Collapse>
            )
        }
    }, {
        name: "availabilityRequirement",
        custom_label: q3Text,
        options: ["Low", "Medium", "High", "Not Defined"],
        helpTexts: [
            "Loss of Availability is likely to have only a limited adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
            "Loss of Availability is likely to have a serious adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
            "Loss of Availability is likely to have a catastrophic adverse effect on the organization or individuals associated with the organization (e.g., employees, customers).",
            "Assigning this value indicates there is insufficient information to choose one of the other values. It has the same effect on scoring as assigning Medium."
        ],
        ...commonpProps
    }, {
        name: "availability",
        custom_label: q4Text,
        /** Removed "Not defined" on discussion with Anant */
        options: [/* "Not defined",  */"None", "Low", "High"],
        helpTexts: [/* "",  */"There is no impact.", "Performance is reduced or interruptions to the service may happen.", "There is total loss of functionality."],
        ...commonpProps
    }, {
        name: "attackingVector",
        custom_label: q5Text,
        options: ["Network", "Local"],
        helpTexts: [<>The  <Vocabulary name={vocabulary} /> is accessible via the Internet.</>, <>The  <Vocabulary name={vocabulary} /> is accessible via the network stack, but is limited to a logically adjacent topology i.e. logical (e.g., local IP subnet) network, or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN to an administrative network zone).</>],
        ...commonpProps
    }];

    const fields = [{
    //     name: "complianceRequired",
    //     label: "Compliance Required",
    //     type: FieldTypes.BOOLEAN,
    //     inline: true
    // }, {
    //     type: FieldTypes.DIVIDER,
    // }, {
        value: "Impact and security control assessment",
        type: FieldTypes.HEADING,
    },
    ...questionFields
        , {
        type: FieldTypes.DIVIDER,
    }, {
        value: "Network and Security control",
        type: FieldTypes.HEADING,
    }, {
        name: "publicCloud",
        label: "Hosted in Public Cloud",
        type: FieldTypes.BOOLEAN,
        inline: true
    }, {
        name: "internetFacing",
        label: "Internet Facing",
        type: FieldTypes.BOOLEAN,
        inline: true
    }]

    const initialValues = {
        publicCloud: true,
        internetFacing: true,
        complianceRequired: true,
    };
    fields.forEach(f => {
        if(f.options){
            initialValues[f.name] = f.options[0];
        }
        if(permission && f.label){
            f.permission = permission
        }
    })

    return {
        fields,
        initialValues,
        qTextArr
    }
}

export function getSpaceBetweenLetterAndNumber(str) {
    if (!isString(str)) {
        return;
    }
    return str.replace(/[a-z](?=\d)|\d(?=[a-z])/gi, '$& ');
}

export const renderName = (email, time) => {
    return (
        <>
            <User userEmail={email} /> on {CellRenderers.TIME(time)}
        </>
    );
}

export const getCommonFilterFields = (dataSource, isUIDateRangeFilter = true) => {
    const commonDateFields = [{
        title: "Updated On",
        dataIndex: "updatedAtDate",
        ...getFilterProps({ dataSource: dataSource, dataIndex: "updatedAtDate", isUIDateRangeFilter, filterType: ColumnFilterTypes.DATE_RANGE })
    }, {
        title: "Created On",
        dataIndex: "createdAtDate",
        ...getFilterProps({ dataSource: dataSource, dataIndex: "createdAtDate", isUIDateRangeFilter, filterType: ColumnFilterTypes.DATE_RANGE })
    }];

    return commonDateFields;
}

export const artifactsValidator = (artifacts) => {
    const isEdit = !isEmpty(artifacts) && some(artifacts, (artifact) => artifact.isEdit);
    if (isEdit) {
        return Promise.reject("Please Save artifacts");
    }
    return Promise.resolve();
};

export const getTierOptions = (tierCategory) => {
    return _.sortBy(
        Object.keys(tierCategory).map((t) => ({
            label: _.upperFirst(getSpaceBetweenLetterAndNumber(t)),
            value: t,
        })),
        "label"
    );
}

export const CLASS_TYPE_DEFAULT_OPTIONS = ["Software only", "Software & Hardware"]
export const CLASS_TYPE_INFRA_OPTIONS = ["Cloud infrastructure", "Traditional infrastructure"];

export const getDefaultTierValues = (currentCategorization, tierCategory) => {
    const selectedTierGlobalFields = tierCategory[currentCategorization];
    const categorizationConfidentiality = getConfidentialOptions(
        { tierCategory: { [currentCategorization]: selectedTierGlobalFields } },
        currentCategorization
    );
    return {
        selectedTierGlobalFields,
        categorizationConfidentiality
    }
};

export const checkAndSetConfidentialityData = (
    data,
    setConfidentialityData
) => {
    if (
        isString(data.confidentialityOptions) &&
        data.confidentialityOptions.length > 0
    ) {
        const _confidentialityData = {};
        data.confidentialityOptions.split(",").forEach((key) => {
            _confidentialityData[key.trim()] = true;
        });
        if(!setConfidentialityData){
            return _confidentialityData;
        }
        setConfidentialityData(_confidentialityData);
    } else {
        if(!setConfidentialityData){
            return {};
        }
        setConfidentialityData((data) => {
            // if it already is empty then no need to rerender.
            if (_.isEmpty(data)) {
                return data;
            } else {
                return {};
            }
        });
    }
};

export function appendVersionToName(props) {
    if (isEmpty(props)) {
      return '';
    }
    const { name, versionNumber } = props;
    const { VERSIONS } = getFeatureFlags_UNSAFE();
    if (!VERSIONS) {
        return name;
    }
    return classnames(name, {[`(${versionNumber})`]: !!versionNumber });
}
  
export function removeVersionFromName(props) {
    if (isEmpty(props)) {
        return '';
    }
    const { name, versionNumber } = props;
    const { VERSIONS } = getFeatureFlags_UNSAFE();
    if (!VERSIONS) {
        return name.trim();
    }
    return name.split(` (${versionNumber})`)[0].trim();
}

export const NewVersionPopup = forwardRef(({ onSubmit, afterClose, initialValues }, ref) => {
    const featureFlag = useFeatureFlag();

    const fields = [
        {
            name: 'existingVersion',
            label: 'Existing Version',
            type: FieldTypes.ANTD_DEFAULT_INPUT,
            props: {
                disabled: true,
            }
        },
        {
            name: 'version',
            label: 'New Version',
            type: FieldTypes.ANTD_DEFAULT_INPUT,
            required: true,
            NoDefaultLengthRule: true,
            rules: [{
                max: 128,
                message: "Maximum length is 128 characters"
            }]
        },
        {
            name: "status",
            label: "Status",
            type: FieldTypes.SELECT,
            required: true,
            props: {
                api: GlobalSettingAPI.getCustomizedStatus,
                placeholder: "Select status",
            }
        },
        ...(featureFlag.VERSIONS ? [{
            name: "cloneManualFindings",
            label: <>Clone Manual Findings</>,
            type: FieldTypes.SWITCH,
            inline: true,
        }] : [])
    ];

    return (
        <BaseModalForm
            onOk={onSubmit}
            ref={ref}
            title='Create New Version'
            fields={fields}
            initialValues={initialValues}
            afterClose={afterClose}
        />
    )
})


export const CopyVersionPopup = forwardRef(({ subProduct, afterClose }, ref) => {
    const initialValues = {
        existingVersion: subProduct.versionNumber
    };

    const onSubmit = async (data) => {
        const response = await SubProductAPI.migrateTriage({ subProductId: +subProduct.id, versionSubProductId: data.versionSubProductId }).promise;
        return response;
    };

    const fields = [
        {
            name: 'existingVersion',
            label: 'Existing Version',
            type: FieldTypes.ANTD_DEFAULT_INPUT,
            props: {
                disabled: true,
            }
        },
        {
            name: 'versionSubProductId',
            label: 'Version To Copy',
            type: FieldTypes.SELECT,
            props: {
                api: SubProductAPI.getVersions,
                apiParams: subProduct.id
            },
            required: true,
        },
    ];

    return (
        <BaseModalForm
            onOk={onSubmit}
            ref={ref}
            title='Copy Triage Details from Version'
            fields={fields}
            initialValues={initialValues}
            afterClose={afterClose}
        />
    )
});

export function syncTierFromTags({ deletedTag, form }) {
    const tierTags = form.getFieldValue("tierTags");
    const newTierTags = _.filter(
      tierTags,
      (t) => getTagKey(t) !== deletedTag
    );
    if (newTierTags.length !== tierTags?.length) {
        form.setFieldsValue({
            tierTags: newTierTags,
            tier: CUSTOM_TIER
        });
    }
}
  
export function syncTagsFromTier({oldTier, newTier, tierBasedTags, tags, form, otherValues, oldTags, newTags, isNewCustomTiers }){
    // send oldTier to remove old tier tags
    const oldTierTags = oldTier ? isNewCustomTiers ? _.map(_.find(tierBasedTags, { id: oldTier })?.tags ?? [], getTagKey) : _.map(tierBasedTags[oldTier], getTagKey) : [];

    // send newTier to add new tier tags
    const newTierTags = newTier ? isNewCustomTiers ? _.map(_.find(tierBasedTags, { id: newTier })?.tags ?? [], getTagKey) : _.map(tierBasedTags[newTier], getTagKey) : [];

    const finalTags = _.uniq(
        _.compact([
            ..._.difference(tags, oldTierTags, _.map(oldTags, getTagKey)),
            ...newTierTags,
            ..._.map(newTags, getTagKey),
        ])
    );
    form.setFieldsValue({ tags: finalTags, ...otherValues });
}

export const getDefaultEnvironment = (globalSettings) => {
  const envFromLocalstorage = getLocalStorage('dashboardEnv', "Production");
  const envValue = globalSettings.disabledEnvironment[0] === "Production" ? "Staging" : "Production";

  return !isEmpty(globalSettings.disabledEnvironment) ? envValue : envFromLocalstorage;
}

export function getSyncedTierFromTags({ deletedTag, allTierBasedTags, tags }) {
    const tagKeyMap = getTagKeyMap(allTierBasedTags);
    const tierTagKeys = map(allTierBasedTags, getTagKey);
    const selectedTierTags = intersection(tags, tierTagKeys);
    const tierTags =  selectedTierTags.map(
        (key) => tagKeyMap[key],
        );
    const newTierTags = _.filter(
      tierTags,
      (t) => getTagKey(t) !== deletedTag
    );
    if (newTierTags.length !== tierTags?.length) {
        return {
            tierTags: newTierTags,
            tier: CUSTOM_TIER
        }
    }
    return {}
}


export function getSyncedTagsFromTier({oldTier, newTier, tierBasedTags, tags, oldTags, newTags, isNewCustomTiers}){
    // send oldTier to remove old tier tags
    const oldTierTags = oldTier ? isNewCustomTiers ? _.map(_.find(tierBasedTags, { id: oldTier })?.tags ?? [], getTagKey) : _.map(tierBasedTags[oldTier], getTagKey) : [];

    // send newTier to add new tier tags
    const newTierTags = newTier ? isNewCustomTiers ? _.map(_.find(tierBasedTags, { id: newTier })?.tags ?? [], getTagKey) : _.map(tierBasedTags[newTier], getTagKey) : [];

    const finalTags = _.uniq(
        _.compact([
            ..._.difference(tags, oldTierTags, _.map(oldTags, getTagKey)),
            ...newTierTags,
            ..._.map(newTags, getTagKey),
        ])
    );
    return finalTags;
}

export const getTierTags = ({details, allTierBasedTags, tags }) => {
    if (details) {
        const tagKeyMap = getTagKeyMap(allTierBasedTags);
        const tierTagKeys = map(allTierBasedTags, getTagKey);
        const selectedTierTags = intersection(tags, tierTagKeys);
        return selectedTierTags.map((key) => tagKeyMap[key])
    }
};

export const renderArmorcodeGroupTags = ({tags, group }) => {
    let newTags = tags
    .filter((tag) => {
        if (group === ALL_HIERARCHY_KEY || !group) {
            return tag.startsWith(`${ARMORCODE_GROUP_KEY}:`);
        }
        return `${tag}/`.startsWith(
            `${ARMORCODE_GROUP_KEY}:${renderGroups(group)}/`,
        );
    })
    .map((tag) => {
        if (tag === `${ARMORCODE_GROUP_KEY}:${renderGroups(group)}`) {
            return "/";
        }
        return getHierarchyName(tag).replace(renderGroups(group), "");
    })
    .sort((a, b) =>
        a.localeCompare(b, "en", { numeric: true }),
    );

    if (newTags.length === 0) {
        return NOT_AVAILABLE;
    }
    if (!(group === ALL_HIERARCHY_KEY || !group)) {
        newTags = newTags.map(tag => `.${tag}`);
    }
    return (
        <ACResponsiveTags tags={newTags} secondaryLabelDelimiter=":" />
    );
};

export const renderTimeStamp = (timeStamp) => moment(timeStamp).format("MMM D, YYYY hh:mmA");